import {
  ArrowRight,
  KeyRound,
  LogOut,
  ShoppingBag,
  User,
} from 'lucide-react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Avatar } from '../../components/Avatar';
import { Logo } from '../../components/Logo';
import { useBrand } from '../../brand/BrandProvider';
import { useStaffContext, usePosAccess } from '../../api/hooks';
import { openPos } from '../../api/pos';
import { logout } from '../../api/auth';

/**
 * Staff (POS-only) landing — §6.4
 * Centered minimal shell. Big "Open POS" CTA, plus profile / change-password / log out links.
 * Identity + business come from GET /staff/context (de-mocked #56); the POS host is
 * `<slug>.<brand.domain>` and "Open POS" runs the SSO handoff (openPos).
 */
export function StaffLanding() {
  const brand = useBrand();
  const navigate = useNavigate();
  const { data, isLoading } = useStaffContext();
  // Trial-ended / suspended businesses lose the POS door (the shell banner explains why).
  const { blocked: posBlocked } = usePosAccess(data?.businessId);

  // Defensive fallbacks so the page renders gracefully if a field is missing.
  const staffName = data?.staffName ?? '—';
  const roleLabel = data?.roleLabel ?? 'Staff';
  const businessName = data?.businessName ?? 'your business';
  const posHost = data?.slug ? `${data.slug}.${brand.domain}` : null;
  const adminName = data?.adminName ?? null;

  async function handleLogout() {
    await logout();
    void navigate({ to: '/login' });
  }

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark flex flex-col">
      {/* Top bar */}
      <header className="px-4 sm:px-8 py-4 flex items-center justify-between border-b border-zinc-200 dark:border-zinc-700">
        <div className="flex items-center gap-2.5">
          <Logo />
          <span className="font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            {businessName}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 transition">
            <Avatar name={staffName} size="sm" />
            <span className="hidden sm:inline text-sm font-medium text-zinc-900 dark:text-zinc-100">
              {staffName}
            </span>
          </div>
        </div>
      </header>

      <main className="flex-1 grid place-items-center px-4 py-12">
        <div className="w-full max-w-md text-center">
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Welcome to {businessName} POS
          </h1>
          <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-300">
            You're signed in as{' '}
            <span className="font-medium text-zinc-900 dark:text-zinc-100">{roleLabel}</span>.
          </p>

          {/* Hero CTA card */}
          <div className="mt-8 rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-950 p-6 sm:p-8 shadow-sm">
            <div className="w-14 h-14 mx-auto mb-5 grid place-items-center rounded-2xl bg-brand text-white dark:bg-brand-dm dark:text-zinc-950">
              <ShoppingBag className="w-7 h-7" />
            </div>

            <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
              Ready to ring up sales?
            </h2>
            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
              {posHost ? (
                <>
                  Opens at <span className="mono">{posHost}</span>
                </>
              ) : (
                'Opens your point of sale'
              )}
            </p>

            {posBlocked ? (
              <p className="mt-6 px-4 py-3.5 rounded-md bg-rose-50 dark:bg-rose-950/40 text-sm text-rose-700 dark:text-rose-300">
                POS access is paused — the subscription is not active. Ask your business admin to settle the bill.
              </p>
            ) : (
              <button
                type="button"
                disabled={isLoading || !data?.businessId}
                onClick={() => openPos(data?.businessId, '/home')}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 px-6 py-3.5 rounded-md text-base font-medium bg-brand text-white shadow-xs hover:bg-brand-hover dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Open POS
                <ArrowRight className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Quick links */}
          <div className="mt-8 pt-6 border-t border-zinc-200 dark:border-zinc-700">
            <ul className="grid sm:grid-cols-3 gap-2">
              <QuickLink icon={User} label="Profile" to="/profile" />
              <QuickLink icon={KeyRound} label="Change password" to="/profile" />
              <li>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                >
                  <LogOut className="w-4 h-4" />
                  Log out
                </button>
              </li>
            </ul>
          </div>

          {adminName && (
            <p className="mt-10 text-[11px] text-zinc-500 dark:text-zinc-400">
              Need help? Ask{' '}
              <span className="font-medium text-zinc-700 dark:text-zinc-200">{adminName}</span>{' '}
              (your business admin).
            </p>
          )}
        </div>
      </main>
    </div>
  );
}

function QuickLink({
  icon: Icon,
  label,
  to,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  to: '/profile' | '/login';
}) {
  return (
    <li>
      <Link
        to={to}
        className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
      >
        <Icon className="w-4 h-4" />
        {label}
      </Link>
    </li>
  );
}
